import { useState } from "react";
import { Link, useMatches } from "@tanstack/react-router";
import {
  Image as ImageIcon,
  Map,
  ClipboardList,
  FileText,
  ImagePlus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { FloatingActionButton } from "@/components/common/floating-action-button/floating-action-button";
import {
  NAV_HEIGHT_PX,
  RAIL_WIDTH_PX,
} from "@/components/common/bottom-nav/nav-constants";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { PROJECT_TABS, type ProjectTab } from "@/lib/project-tabs";
import type { Action } from "@/types/permissions";
import { useRecordCapture } from "@/components/common/record-capture/use-record-capture";
import { RecordLimitBlock } from "@/components/records/record-limit-block/record-limit-block";
import { BulkActionBar } from "@/components/records/selection-mode/bulk-action-bar";
import { useSelectionMode } from "@/components/records/selection-mode/selection-mode-provider";
import type { ImageViewerModalProps } from "@/components/common/image-viewer-modal/types";
import { useRecordPanelInset } from "@/components/records/records-view/record-panel-inset";
import type { ProjectMetadataField, ProjectPlan, ProjectTag } from "@/types/api";

// Four project surfaces, in the order work moves through them: capture,
// locate, chase, report (`PROJECT_TABS`). The former Projects tab moved into
// the avatar menu as "Switch project" — see [[project-header.spec]] — because
// leaving the project is where the other leave-this-project actions already
// live, and a fifth tab would crowd the strip the camera FAB already sits in.
// The bottom bar and the rail both draw the one list they are given, so they
// cannot disagree.
const TAB_ICONS: Record<ProjectTab["key"], React.ComponentType<{ className?: string }>> = {
  photos: ImageIcon,
  plans: Map,
  issues: ClipboardList,
  reports: FileText,
};

interface BottomNavProps {
  projectId: string;
  /**
   * The tabs this user may use, in tab order. With one or none there is
   * nowhere to switch to, so neither the bar nor the rail is drawn. See
   * [[bottom-nav.spec]] § Tabs follow permissions.
   */
  tabs?: readonly ProjectTab[];
  can: (action: Action) => boolean;
  canOnResource: ImageViewerModalProps["canOnResource"];
  metadataFields?: ProjectMetadataField[];
  projectTags?: ProjectTag[];
  projectPlans?: ProjectPlan[];
  recordsCount: number;
  maxRecordCount: number;
}

export function BottomNav({
  projectId,
  tabs = PROJECT_TABS,
  can,
  canOnResource,
  metadataFields = [],
  projectTags = [],
  projectPlans = [],
  recordsCount,
  maxRecordCount,
}: BottomNavProps) {
  const matches = useMatches();
  const currentPath = matches[matches.length - 1]?.fullPath ?? "";

  const canCreateRecord = can("record:Create");
  const canCreateReport = can("report:Create");
  const canCreateIssue = can("issue:Create");
  const { isSelectionMode } = useSelectionMode();
  const recordPanelInset = useRecordPanelInset();
  // From 768px the navigation is a rail down the left; below, the bottom bar.
  // One component draws both so they share one capture flow, which survives
  // the window crossing the breakpoint. See [[bottom-nav.spec]] § Layout.
  const isDesktop = useIsDesktop();

  // Upload FAB only on the records page (records-page.spec § US-004).
  // TanStack's `fullPath` is the template with a trailing slash
  // ("/projects/$projectId/records/"), so `startsWith` covers both the
  // exact match and any future nested sub-routes.
  const isOnRecordsPage = currentPath.startsWith(
    "/projects/$projectId/records/",
  );

  // `max_record_count === 0` means "no limit configured" — don't block.
  const isAtLimit = maxRecordCount > 0 && recordsCount >= maxRecordCount;
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);

  const { openCamera, openLibrary, captureUI } = useRecordCapture({
    projectId,
    canOnResource,
    canCreate: canCreateRecord,
    canCreateTag: can("tag:Create"),
    canCreateIssue,
    metadataFields,
    projectTags,
    projectPlans,
  });

  const handleCameraClick = () => {
    if (isAtLimit) {
      setIsLimitModalOpen(true);
      return;
    }
    openCamera();
  };

  const handleUploadClick = () => {
    if (isAtLimit) {
      setIsLimitModalOpen(true);
      return;
    }
    openLibrary();
  };


  function isActive(tabTo: string): boolean {
    return currentPath === tabTo || currentPath.startsWith(tabTo + "/");
  }

  // Kept first in every branch below, so an open capture flow keeps its place
  // in the tree whatever changes around it: selection mode starting, or the
  // window crossing 768px.
  const capture = canCreateRecord && captureUI;
  const drawsTabs = tabs.length > 1;
  const rail = isDesktop && drawsTabs && (
    <NavRail projectId={projectId} tabs={tabs} isActive={isActive} />
  );

  // Selection mode: on a phone the bulk action bar takes over the bottom
  // navbar's strip. From 768px the rail stays and the bar runs beside it. The
  // FABs are hidden either way so the bar can claim the bottom per
  // [[selection-mode.spec]] § Layout.
  if (isSelectionMode) {
    return (
      <>
        {capture}
        {rail}
        <BulkActionBar
          canCreateReport={canCreateReport}
          canCreateIssue={canCreateIssue}
          projectId={projectId}
        />
      </>
    );
  }

  return (
    <>
      {capture}

      {rail}

      {canCreateRecord && isOnRecordsPage && (
        // Upload FAB shares the camera FAB's useRecordCapture instance
        // (openLibrary triggers the library-pick input) so we don't stand up
        // a parallel capture flow. z-50 keeps it above the bottom nav, which
        // shares the same stacking context.
        <FloatingActionButton
          zIndexClassName="z-50"
          onClick={handleUploadClick}
          // Clear of the record side panel when one is open on desktop.
          style={recordPanelInset ? { right: recordPanelInset + 16 } : undefined}
        >
          <ImagePlus className="h-4 w-4" />
          Upload
        </FloatingActionButton>
      )}

      {!isDesktop && (
        <>
          {canCreateRecord && (
            <div
              className="fixed left-1/2 z-[51] -translate-x-1/2"
              style={{
                bottom: `calc(${NAV_HEIGHT_PX / 2}px + env(safe-area-inset-bottom, 0px))`,
              }}
            >
              <button
                type="button"
                aria-label="Take photo"
                onClick={handleCameraClick}
                className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-primary shadow-lg transition-all duration-100 hover:opacity-90 active:scale-90"
              >
                <ShutterIcon />
              </button>
            </div>
          )}

          {drawsTabs && (
            <nav
              aria-label="Project"
              className="pb-safe fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background"
              style={{
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
              }}
            >
              <PhoneTabs
                projectId={projectId}
                tabs={tabs}
                isActive={isActive}
                withCameraGap={canCreateRecord}
              />
            </nav>
          )}
        </>
      )}

      <RecordLimitBlock
        open={isLimitModalOpen}
        onOpenChange={setIsLimitModalOpen}
        maxRecordCount={maxRecordCount}
      />
    </>
  );
}

/**
 * The bottom bar's tabs. With the camera, they split into a left and a right
 * half around its gap, which is therefore dead centre — where the camera is
 * anchored — however many tabs there are; the left half takes the odd one.
 * Without it, they spread evenly.
 */
function PhoneTabs({
  projectId,
  tabs,
  isActive,
  withCameraGap,
}: {
  projectId: string;
  tabs: readonly ProjectTab[];
  isActive: (to: string) => boolean;
  withCameraGap: boolean;
}) {
  const drawTab = (tab: ProjectTab) => (
    <NavTab
      key={tab.key}
      label={tab.label}
      icon={TAB_ICONS[tab.key]}
      to={tab.to}
      params={{ projectId }}
      active={isActive(tab.to)}
    />
  );

  if (!withCameraGap) {
    return (
      <div
        className="mx-auto flex max-w-md items-center justify-around px-2 pb-1 pt-2"
        style={{ height: `${NAV_HEIGHT_PX}px` }}
      >
        {tabs.map(drawTab)}
      </div>
    );
  }

  const half = Math.ceil(tabs.length / 2);
  return (
    <div
      className="mx-auto grid max-w-md grid-cols-[1fr_auto_1fr] items-center px-2 pb-1 pt-2"
      style={{ height: `${NAV_HEIGHT_PX}px` }}
    >
      <div className="flex items-center justify-around">
        {tabs.slice(0, half).map(drawTab)}
      </div>
      <div className="w-14" aria-hidden="true" data-slot="camera-gap" />
      <div className="flex items-center justify-around">
        {tabs.slice(half).map(drawTab)}
      </div>
    </div>
  );
}

interface NavTabProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  params?: Record<string, string>;
  active: boolean;
}

/** Inactive tabs: muted, and in dark mode dimmed to ~85% (5.9:1 on the canvas) so the one mint icon is what the eye lands on. */
const NAV_INACTIVE = "text-muted-foreground dark:text-muted-foreground/85";

function NavTab({ label, icon: Icon, to, params, active }: NavTabProps) {
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- routes registered dynamically; Link's strict types can't match
      to={to as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params={params as any}
      className={cn(
        "flex flex-col items-center justify-center gap-1 px-2 py-1 transition-transform duration-100 active:scale-90",
        active ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {/* Dark mode marks the active tab by hue: a mint icon, the rest dimmed.
          See [[bottom-nav.spec]] and [[theming]]. */}
      <Icon className={cn("h-5 w-5", active ? "text-foreground dark:text-primary" : NAV_INACTIVE)} />
      <span
        className={cn(
          "text-xs",
          active ? "text-foreground font-medium" : NAV_INACTIVE,
        )}
      >
        {label}
      </span>
    </Link>
  );
}

interface NavRailProps {
  projectId: string;
  tabs: readonly ProjectTab[];
  isActive: (to: string) => boolean;
}

/**
 * The navigation from 768px: an in-flow column down the left, under the
 * header, so the page starts at its edge rather than running under it. The
 * permitted tabs, each an icon with its label below; the active one sits on a pill,
 * with a bar on the rail's left edge. No camera button: that is the phone's
 * alone. See [[bottom-nav.spec]] § Layout / US-007.
 */
function NavRail({ projectId, tabs, isActive }: NavRailProps) {
  return (
    <nav
      aria-label="Project"
      data-slot="nav-rail"
      className="flex shrink-0 flex-col items-center gap-1 overflow-y-auto border-r border-border bg-background pt-3 pb-4"
      style={{ width: RAIL_WIDTH_PX }}
    >
      {tabs.map((tab) => (
        <RailTab
          key={tab.key}
          label={tab.label}
          icon={TAB_ICONS[tab.key]}
          to={tab.to}
          params={{ projectId }}
          active={isActive(tab.to)}
        />
      ))}
    </nav>
  );
}

function RailTab({ label, icon: Icon, to, params, active }: NavTabProps) {
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- routes registered dynamically; Link's strict types can't match
      to={to as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params={params as any}
      className="group relative flex w-full flex-col items-center gap-1 py-1.5 transition-transform duration-100 active:scale-95"
    >
      {/* The active tab's bar on the rail's left edge: as tall as the icon and
          label together (the link's own py-1.5 is excluded), right corners
          rounded. See [[bottom-nav.spec]] US-007. */}
      {active && (
        <span
          aria-hidden="true"
          data-slot="rail-active-bar"
          className="absolute inset-y-1.5 left-0 w-1 rounded-r-full bg-foreground dark:hidden"
        />
      )}
      <span
        data-slot="rail-indicator"
        className={cn(
          "flex h-8 w-14 items-center justify-center rounded-full transition-colors",
          active
            ? "bg-secondary dark:bg-primary/[0.13]"
            : "group-hover:bg-secondary/60 dark:group-hover:bg-card-hover",
        )}
      >
        <Icon
          className={cn(
            "h-5 w-5",
            active ? "text-foreground dark:text-primary" : NAV_INACTIVE,
          )}
        />
      </span>
      <span
        className={cn(
          "text-xs",
          active ? "font-medium text-foreground" : NAV_INACTIVE,
        )}
      >
        {label}
      </span>
    </Link>
  );
}

/** The app's shutter mark, drawn inside the camera button. */
function ShutterIcon() {
  return (
    <svg
      className="h-[85%] w-[85%]"
      width="55"
      height="55"
      viewBox="0 0 55 55"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M25.9547 45.3091C26.464 45.3527 26.9794 45.375 27.5 45.375C32.0512 45.375 36.2051 43.6741 39.3608 40.8733L44.2381 49.3211C39.6019 52.8827 33.7982 55 27.5 55C25.1998 55 22.9655 54.7176 20.8299 54.1855L25.9547 45.3091Z"
        fill="#F4F4F4"
      />
      <path
        d="M11.408 35.2916C13.474 39.5507 17.1811 42.866 21.7075 44.4156L16.8348 52.8554C9.2907 49.6783 3.4611 43.2325 1.1194 35.2916H11.408Z"
        fill="#F4F4F4"
      />
      <path
        d="M12.5942 17.6307C10.7179 20.4588 9.625 23.8518 9.625 27.5C9.625 28.7567 9.75469 29.9831 10.0014 31.1666H0.242309C0.082495 29.9671 0 28.7432 0 27.5C0 20.2265 2.8238 13.6125 7.43492 8.69448L12.5942 17.6307Z"
        fill="#F4F4F4"
      />
      <path
        d="M28.7544 9.66833C28.34 9.6396 27.9217 9.625 27.5 9.625C22.8517 9.625 18.6179 11.3992 15.4387 14.3074L10.5513 5.84225C15.2218 2.18211 21.1061 0 27.5 0C29.7048 0 31.849 0.259463 33.9037 0.749519L28.7544 9.66833Z"
        fill="#F4F4F4"
      />
      <path
        d="M43.592 19.7083C41.4873 15.3696 37.6796 12.0104 33.0377 10.4994L37.9201 2.0428C45.5829 5.18262 51.5136 11.6816 53.8806 19.7083L43.592 19.7083Z"
        fill="#F4F4F4"
      />
      <path
        d="M44.9986 23.8333C45.2453 25.0168 45.375 26.2433 45.375 27.5C45.375 31.2535 44.2181 34.7368 42.2414 37.6127L47.3757 46.5055C52.0988 41.5676 55 34.8726 55 27.5C55 26.2568 54.9175 25.0328 54.7577 23.8333L44.9986 23.8333Z"
        fill="#F4F4F4"
      />
    </svg>
  );
}
